'use client'

import ConfirmModal from '@/components/confirm-modal'
import ExamPicker, { SelectedExam } from '@/components/medical/exam-picker'
import MedicationListEditor, {
    emptyMedication,
    MedicationItem,
} from '@/components/medical/medication-list-editor'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import RichTextEditor from '@/components/rich-text-editor'
import { getCatalog } from '@/lib/api/catalog/catalog-api'
import { IcdCode, searchIcdCodes } from '@/lib/api/prontuario/prontuario-api'
import {
    createTemplate,
    CreateTemplateData,
    deleteTemplate,
    getTemplates,
    getTemplateVariables,
    Template,
    TemplateCategory,
    TemplateVariable,
    TEMPLATE_CATEGORY_HINTS,
    TEMPLATE_CATEGORY_LABELS,
    updateTemplate,
} from '@/lib/api/templates/template-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdBiotech,
    MdClose,
    MdContentCopy,
    MdDelete,
    MdDescription,
    MdEdit,
    MdMedication,
    MdReceiptLong,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'

const CATEGORY_ICONS: Record<TemplateCategory, typeof MdBiotech> = {
    [TemplateCategory.EXAM_REQUEST]: MdBiotech,
    [TemplateCategory.REPORT]: MdDescription,
    [TemplateCategory.CERTIFICATE]: MdReceiptLong,
    [TemplateCategory.PRESCRIPTION]: MdMedication,
}

interface FormState {
    name: string
    category: TemplateCategory
    professionalId: number | null
    content: string
    notes: string
    icdCodeId: number | null
}

const defaultForm: FormState = {
    name: '',
    category: TemplateCategory.EXAM_REQUEST,
    professionalId: null,
    content: '',
    notes: '',
    icdCodeId: null,
}

/**
 * Modelos de documento — pedido de exames, laudo e atestado.
 *
 * O que o médico repete a cada paciente: o mesmo grupo de exames de check-up, o mesmo
 * texto de relatório, o mesmo atestado. Em vez de digitar de novo (ou copiar do
 * paciente anterior, que é como o erro entra), ele monta uma vez e aplica.
 *
 * O modelo é do PROFISSIONAL, não da clínica: o documento sai com o CRM de quem
 * assina, e o texto de um neurocirurgião não serve ao colega. A secretária pode montar
 * pelo médico — escolhendo-o no seletor —, e é por isso que o profissional é um campo
 * do formulário e não o usuário logado.
 */
export default function TemplatesPage() {
    const { user, isProfessional } = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'templates'
    )

    const [templates, setTemplates] = useState<Template[]>([])
    const [loading, setLoading] = useState(true)
    const [variables, setVariables] = useState<TemplateVariable[]>([])

    const [search, setSearch] = useState('')
    const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | ''>(
        ''
    )
    const [professionalFilter, setProfessionalFilter] = useState<number | null>(
        null
    )

    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<FormState>(defaultForm)
    const [selectedExams, setSelectedExams] = useState<SelectedExam[]>([])
    const [medications, setMedications] = useState<MedicationItem[]>([])
    const [saving, setSaving] = useState(false)

    const [icdQuery, setIcdQuery] = useState('')
    const [icdResults, setIcdResults] = useState<IcdCode[]>([])
    const [selectedIcd, setSelectedIcd] = useState<IcdCode | null>(null)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<Template | null>(null)

    /**
     * Modelo que originou a cópia em edição.
     *
     * Duplicar não é fim em si: quem duplica quer o modelo do colega COM um ajuste —
     * outro CID, um exame a mais, a dose diferente. Por isso a cópia abre no
     * formulário inteiro, e não num modal de confirmação; e por isso ela só existe
     * depois de salva: desistir no meio não deve deixar "(cópia)" na listagem.
     */
    const [copyingFrom, setCopyingFrom] = useState<Template | null>(null)

    useEffect(() => {
        loadTemplates()
    }, [professionalFilter, categoryFilter])

    useEffect(() => {
        loadVariables()
    }, [])

    const loadTemplates = async () => {
        setLoading(true)
        try {
            const response = await getTemplates({
                professionalId: professionalFilter ?? undefined,
                category: categoryFilter || undefined,
            })
            if (response.success) {
                setTemplates(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar os modelos')
            }
        } catch (error) {
            toast.error('Erro ao carregar os modelos')
        } finally {
            setLoading(false)
        }
    }

    const loadVariables = async () => {
        try {
            const response = await getTemplateVariables()
            if (response.success) setVariables(response.result || [])
        } catch (error) {
            // A barra de variáveis some, o editor continua funcionando: o médico pode
            // digitar `{{paciente}}` à mão. Não vale interromper o cadastro por isso.
        }
    }

    const visibleTemplates = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return templates
        return templates.filter(template =>
            template.name.toLowerCase().includes(term)
        )
    }, [templates, search])

    const isExamRequest = form.category === TemplateCategory.EXAM_REQUEST
    const isPrescription = form.category === TemplateCategory.PRESCRIPTION

    const openCreateModal = () => {
        setEditingId(null)
        setCopyingFrom(null)
        setForm({
            ...defaultForm,
            // O médico logado já vem preenchido; a secretária escolhe.
            professionalId: isProfessional
                ? (user?.professionalId ?? null)
                : null,
        })
        setSelectedExams([])
        setMedications([{ ...emptyMedication }])
        setSelectedIcd(null)
        setIcdQuery('')
        setModalOpen(true)
    }

    const openEditModal = (template: Template) => openTemplateModal(template)

    /**
     * Abre o formulário sobre um modelo existente — para editá-lo ou para copiá-lo.
     *
     * É o mesmo carregamento nos dois casos: a cópia difere só por não ter `id` (o
     * salvar cria em vez de atualizar) e por nascer com o sufixo no nome.
     */
    const openDuplicateModal = (template: Template) =>
        openTemplateModal(template, { asCopy: true })

    const openTemplateModal = async (
        template: Template,
        options?: { asCopy?: boolean }
    ) => {
        const asCopy = options?.asCopy ?? false
        setCopyingFrom(asCopy ? template : null)
        setEditingId(asCopy ? null : template.id)
        setForm({
            name: asCopy ? `${template.name} (cópia)` : template.name,
            category: template.category,
            // Duplicar o modelo do colega cria um modelo NOVO, e o dono dele é
            // quem duplicou. Na edição o dono continua sendo o do registro.
            professionalId:
                asCopy && isProfessional
                    ? (user?.professionalId ?? template.professionalId)
                    : template.professionalId,
            content: template.content || '',
            notes: template.notes || '',
            icdCodeId: template.icdCodeId ?? null,
        })
        setSelectedIcd(
            template.icdCode
                ? ({
                      id: template.icdCode.id,
                      code: template.icdCode.code,
                      description: template.icdCode.description,
                  } as IcdCode)
                : null
        )
        setIcdQuery('')
        setMedications(
            (template.medications ?? []).map(item => ({
                medication: item.medication,
                dosage: item.dosage ?? '',
                frequency: item.frequency ?? '',
                duration: item.duration ?? '',
                instructions: item.instructions ?? '',
                quantity: item.quantity ? String(item.quantity) : '',
            }))
        )
        setModalOpen(true)

        // O `ExamPicker` trabalha com o `Catalog` inteiro, e a listagem do modelo só
        // guarda o essencial. Buscar aqui é o que permite reabrir o modelo com os
        // exames marcados — inclusive os que não estão na primeira página da busca.
        if (template.category === TemplateCategory.EXAM_REQUEST) {
            const loaded = await Promise.all(
                template.items.map(async item => {
                    const response = await getCatalog(item.catalogId)
                    if (!response.success || !response.result) return null
                    return {
                        catalog: response.result,
                        variations: (item.variationCatalogIds || []).map(
                            id => ({
                                id,
                                name: '',
                            })
                        ),
                    } as SelectedExam
                })
            )
            setSelectedExams(loaded.filter(Boolean) as SelectedExam[])
        } else {
            setSelectedExams([])
        }
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingId(null)
        setCopyingFrom(null)
        setForm(defaultForm)
        setSelectedExams([])
        setMedications([])
        setSelectedIcd(null)
    }

    const handleIcdSearch = async (query: string) => {
        setIcdQuery(query)
        if (query.trim().length < 2) {
            setIcdResults([])
            return
        }
        try {
            const response = await searchIcdCodes(query)
            if (response.success) setIcdResults(response.result || [])
        } catch (error) {
            setIcdResults([])
        }
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()

        if (!form.name.trim()) {
            toast.error('Dê um nome ao modelo')
            return
        }
        // O profissional logado é o dono: ele não escolhe (nem vê) o seletor.
        const professionalId = isProfessional
            ? (form.professionalId ?? user?.professionalId ?? null)
            : form.professionalId
        if (!professionalId) {
            toast.error('Escolha o profissional dono do modelo')
            return
        }
        if (isExamRequest && selectedExams.length === 0) {
            toast.error('Escolha ao menos um exame para o modelo')
            return
        }
        if (!isExamRequest && !form.content.trim()) {
            toast.error('Escreva o texto do modelo')
            return
        }

        // Linha em branco é o normal ao abrir o formulário; só o que tem nome vai.
        const filledMedications = medications.filter(item =>
            item.medication.trim()
        )

        const payload: CreateTemplateData = {
            name: form.name.trim(),
            category: form.category,
            professionalId,
            icdCodeId: form.icdCodeId ?? undefined,
            ...(isExamRequest
                ? {
                      notes: form.notes.trim() || undefined,
                      items: selectedExams.map((item, index) => ({
                          catalogId: item.catalog.id,
                          variationCatalogIds: item.variations.map(v => v.id),
                          sortOrder: index,
                      })),
                  }
                : { content: form.content }),
            ...(isPrescription
                ? {
                      notes: form.notes.trim() || undefined,
                      medications: filledMedications.map(item => ({
                          medication: item.medication.trim(),
                          dosage: item.dosage.trim() || undefined,
                          frequency: item.frequency.trim() || undefined,
                          duration: item.duration.trim() || undefined,
                          instructions: item.instructions.trim() || undefined,
                          quantity: item.quantity
                              ? parseInt(item.quantity, 10)
                              : undefined,
                      })),
                  }
                : {}),
        }

        setSaving(true)
        try {
            const response = editingId
                ? await updateTemplate(editingId, payload)
                : await createTemplate(payload)

            if (response.success) {
                toast.success(
                    editingId
                        ? 'Modelo atualizado'
                        : copyingFrom
                          ? 'Cópia salva'
                          : 'Modelo criado'
                )
                await loadTemplates()
                closeModal()
            } else {
                toast.error(response.error || 'Erro ao salvar o modelo')
            }
        } catch (error) {
            toast.error('Erro ao salvar o modelo')
        } finally {
            setSaving(false)
        }
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        try {
            const response = await deleteTemplate(deleteTarget.id)
            if (response.success) {
                toast.success('Modelo removido')
                await loadTemplates()
            } else {
                toast.error(response.error || 'Erro ao remover o modelo')
            }
        } catch (error) {
            toast.error('Erro ao remover o modelo')
        } finally {
            setShowDeleteModal(false)
            setDeleteTarget(null)
        }
    }

    return (
        <div className="p-4 md:p-6 flex flex-col gap-6">
            {/* Cabeçalho */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                        <MdDescription className="text-2xl text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Modelos
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Pedidos de exame, laudos e atestados prontos para
                            reaproveitar
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        type="button"
                        className="btn btn-primary gap-2"
                        onClick={openCreateModal}
                    >
                        <MdAdd className="text-lg" />
                        Novo modelo
                    </button>
                )}
            </div>

            {/* Filtros */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="form-control w-full">
                        <label className="label pb-1">
                            <span className="label-text font-medium">
                                Buscar
                            </span>
                        </label>
                        <div className="relative">
                            <MdSearch className="absolute left-3 top-1/2 z-10 -translate-y-1/2 text-lg text-base-content/40" />
                            <input
                                type="text"
                                className="input input-bordered w-full pl-10"
                                placeholder="Nome do modelo..."
                                value={search}
                                onChange={event =>
                                    setSearch(event.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="form-control w-full">
                        <label className="label pb-1">
                            <span className="label-text font-medium">
                                Categoria
                            </span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={categoryFilter}
                            onChange={event =>
                                setCategoryFilter(
                                    event.target.value as TemplateCategory | ''
                                )
                            }
                        >
                            <option value="">Todas</option>
                            {Object.values(TemplateCategory).map(category => (
                                <option key={category} value={category}>
                                    {TEMPLATE_CATEGORY_LABELS[category]}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-control w-full">
                        <label className="label pb-1">
                            <span className="label-text font-medium">
                                Profissional
                            </span>
                        </label>
                        <ProfessionalSearchSelector
                            value={professionalFilter}
                            onChange={id => setProfessionalFilter(id)}
                            label=""
                            placeholder="Todos os profissionais"
                        />
                    </div>
                </div>
            </div>

            {/* Lista */}
            {loading ? (
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                    <div className="loading loading-spinner loading-lg text-primary"></div>
                    <p className="text-base-content/60 text-sm">
                        Carregando modelos...
                    </p>
                </div>
            ) : visibleTemplates.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-10 text-center">
                    <MdDescription className="text-5xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/70 font-medium">
                        Nenhum modelo cadastrado
                    </p>
                    <p className="text-sm text-base-content/50 mt-1">
                        Comece pelo que você mais repete: o grupo de exames de
                        rotina ou o texto padrão do seu laudo.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {visibleTemplates.map(template => {
                        const Icon = CATEGORY_ICONS[template.category]
                        return (
                            <div
                                key={template.id}
                                className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 flex flex-col gap-3"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                                            <Icon className="text-xl text-primary" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="font-bold text-base-content truncate">
                                                {template.name}
                                            </h3>
                                            <p className="text-xs text-base-content/60">
                                                {
                                                    TEMPLATE_CATEGORY_LABELS[
                                                        template.category
                                                    ]
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {/* Duplicar é `create`, não `update`: o que
                                            nasce é um modelo novo. */}
                                        {canCreate && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Duplicar modelo"
                                                onClick={() =>
                                                    openDuplicateModal(template)
                                                }
                                            >
                                                <MdContentCopy className="text-lg" />
                                            </button>
                                        )}
                                        {canUpdate && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Editar modelo"
                                                onClick={() =>
                                                    openEditModal(template)
                                                }
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square text-error"
                                                title="Remover modelo"
                                                onClick={() => {
                                                    setDeleteTarget(template)
                                                    setShowDeleteModal(true)
                                                }}
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {template.category ===
                                    TemplateCategory.EXAM_REQUEST ? (
                                        <span className="badge badge-ghost gap-1">
                                            <MdBiotech className="text-sm" />
                                            {template.items.length}{' '}
                                            {template.items.length === 1
                                                ? 'exame'
                                                : 'exames'}
                                        </span>
                                    ) : null}
                                    {template.category ===
                                        TemplateCategory.PRESCRIPTION &&
                                    template.medications?.length ? (
                                        <span className="badge badge-ghost gap-1">
                                            <MdMedication className="text-sm" />
                                            {template.medications.length}{' '}
                                            {template.medications.length === 1
                                                ? 'medicamento'
                                                : 'medicamentos'}
                                        </span>
                                    ) : null}
                                    {template.icdCode && (
                                        <span className="badge badge-ghost">
                                            CID {template.icdCode.code}
                                        </span>
                                    )}
                                </div>

                                {template.professional && (
                                    <p className="text-xs text-base-content/50 mt-auto">
                                        {template.professional.name}
                                        {template.professional
                                            .registrationNumber &&
                                            ` — ${template.professional.councilName ?? 'CRM'} ${template.professional.registrationNumber}`}
                                    </p>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Criar / editar / duplicar */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-4xl max-h-[92vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-lg font-bold">
                                    {editingId
                                        ? 'Editar modelo'
                                        : copyingFrom
                                          ? 'Duplicar modelo'
                                          : 'Novo modelo'}
                                </h3>
                                {copyingFrom && (
                                    <p className="text-sm text-base-content/60 mt-1">
                                        Cópia de "{copyingFrom.name}". Ajuste o
                                        que precisar — o original não muda.
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={closeModal}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div
                                className={`grid grid-cols-1 gap-4 ${
                                    isProfessional ? '' : 'md:grid-cols-2'
                                }`}
                            >
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Nome do modelo{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: Check-up anual"
                                        value={form.name}
                                        onChange={event =>
                                            setForm(previous => ({
                                                ...previous,
                                                name: event.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>

                                {/* O profissional logado é o dono do modelo: não há
                                    o que escolher, e o seletor só daria a impressão
                                    de que ele pode criar modelo no nome de outro. */}
                                {!isProfessional && (
                                    <div className="form-control w-full">
                                        <label className="label pb-1">
                                            <span className="label-text font-medium">
                                                Profissional{' '}
                                                <span className="text-error">
                                                    *
                                                </span>
                                            </span>
                                        </label>
                                        <ProfessionalSearchSelector
                                            value={form.professionalId}
                                            onChange={id =>
                                                setForm(previous => ({
                                                    ...previous,
                                                    professionalId: id,
                                                }))
                                            }
                                            label=""
                                            placeholder="De quem é este modelo..."
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Categoria: muda o formulário inteiro, por isso vem cedo
                                e trava na edição e na cópia — nos dois casos o conteúdo
                                já chegou montado, e trocar de categoria o descartaria
                                (os exames marcados, o texto escrito, os medicamentos). */}
                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Categoria{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                                    {Object.values(TemplateCategory).map(
                                        category => {
                                            const Icon =
                                                CATEGORY_ICONS[category]
                                            const active =
                                                form.category === category
                                            return (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    disabled={
                                                        !!editingId ||
                                                        !!copyingFrom
                                                    }
                                                    onClick={() =>
                                                        setForm(previous => ({
                                                            ...previous,
                                                            category,
                                                        }))
                                                    }
                                                    className={`text-left rounded-xl border p-3 transition-colors ${
                                                        active
                                                            ? 'border-primary bg-primary/5'
                                                            : 'border-base-300 hover:border-base-content/30'
                                                    } ${editingId || copyingFrom ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                >
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Icon
                                                            className={`text-lg ${active ? 'text-primary' : 'text-base-content/50'}`}
                                                        />
                                                        <span className="font-medium text-sm">
                                                            {
                                                                TEMPLATE_CATEGORY_LABELS[
                                                                    category
                                                                ]
                                                            }
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-base-content/60">
                                                        {
                                                            TEMPLATE_CATEGORY_HINTS[
                                                                category
                                                            ]
                                                        }
                                                    </p>
                                                </button>
                                            )
                                        }
                                    )}
                                </div>
                            </div>

                            {/* CID: vale para as três categorias — o pedido de exame
                                impresso costuma trazer um, e repetir o mesmo a cada
                                pedido é parte do que o modelo poupa. */}
                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        CID sugerido
                                    </span>
                                </label>
                                {selectedIcd ? (
                                    <div className="flex items-center gap-2">
                                        <span className="badge badge-lg badge-ghost gap-2">
                                            {selectedIcd.code} —{' '}
                                            {selectedIcd.description}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs btn-square"
                                            onClick={() => {
                                                setSelectedIcd(null)
                                                setForm(previous => ({
                                                    ...previous,
                                                    icdCodeId: null,
                                                }))
                                            }}
                                        >
                                            <MdClose className="text-base" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Busque por código ou descrição..."
                                            value={icdQuery}
                                            onChange={event =>
                                                handleIcdSearch(
                                                    event.target.value
                                                )
                                            }
                                        />
                                        {icdResults.length > 0 && (
                                            <div className="mt-2 max-h-40 overflow-y-auto border border-base-300 rounded-lg divide-y divide-base-300/60">
                                                {icdResults.map(icd => (
                                                    <button
                                                        key={icd.id}
                                                        type="button"
                                                        className="w-full text-left px-3 py-2 hover:bg-base-200 text-sm"
                                                        onClick={() => {
                                                            setSelectedIcd(icd)
                                                            setForm(
                                                                previous => ({
                                                                    ...previous,
                                                                    icdCodeId:
                                                                        icd.id,
                                                                })
                                                            )
                                                            setIcdResults([])
                                                            setIcdQuery('')
                                                        }}
                                                    >
                                                        <strong>
                                                            {icd.code}
                                                        </strong>{' '}
                                                        — {icd.description}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Formulário por categoria */}
                            {isExamRequest ? (
                                <div className="space-y-4">
                                    <div className="border border-base-300 rounded-xl overflow-hidden">
                                        <div className="px-4 py-2 bg-base-200/60 border-b border-base-300">
                                            <span className="text-sm font-medium">
                                                Exames do modelo
                                            </span>
                                            <span className="text-sm text-base-content/60 ml-2">
                                                {selectedExams.length === 0
                                                    ? 'nenhum marcado'
                                                    : `${selectedExams.length} marcado${selectedExams.length === 1 ? '' : 's'}`}
                                            </span>
                                        </div>
                                        <ExamPicker
                                            selected={selectedExams}
                                            onSelectedChange={setSelectedExams}
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label pb-1">
                                            <span className="label-text font-medium">
                                                Indicação clínica
                                            </span>
                                        </label>
                                        <textarea
                                            className="textarea textarea-bordered w-full"
                                            rows={3}
                                            placeholder="Texto que acompanha o pedido. Ex: Pós-embolização de aneurisma cerebral."
                                            value={form.notes}
                                            onChange={event =>
                                                setForm(previous => ({
                                                    ...previous,
                                                    notes: event.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                            ) : isPrescription ? (
                                <div className="space-y-4">
                                    {/* Texto puro, não editor rico: a tela do
                                        receituário é uma `textarea` e o PDF passa o
                                        conteúdo por `stripHtml` — marcação aqui só
                                        produziria `<p>` visível para o médico. */}
                                    <div className="form-control w-full">
                                        <label className="label pb-1">
                                            <span className="label-text font-medium">
                                                Texto da receita{' '}
                                                <span className="text-error">
                                                    *
                                                </span>
                                            </span>
                                        </label>
                                        <textarea
                                            className="textarea textarea-bordered w-full"
                                            rows={4}
                                            placeholder="Ex.: Uso oral. Retornar em 7 dias com o resultado."
                                            value={form.content}
                                            onChange={event =>
                                                setForm(previous => ({
                                                    ...previous,
                                                    content: event.target.value,
                                                }))
                                            }
                                        />
                                        <span className="text-xs text-base-content/50 mt-1">
                                            Use {'{{paciente}}'}, {'{{data}}'} e{' '}
                                            {'{{profissional.conselho}}'} — são
                                            trocados pelos dados reais ao aplicar
                                            o modelo.
                                        </span>
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label pb-1">
                                            <span className="label-text font-medium">
                                                Observações
                                            </span>
                                        </label>
                                        <textarea
                                            className="textarea textarea-bordered w-full"
                                            rows={3}
                                            placeholder="Observações que acompanham a receita."
                                            value={form.notes}
                                            onChange={event =>
                                                setForm(previous => ({
                                                    ...previous,
                                                    notes: event.target.value,
                                                }))
                                            }
                                        />
                                    </div>

                                    <div className="border border-base-300 rounded-xl p-4">
                                        <MedicationListEditor
                                            value={medications}
                                            onChange={setMedications}
                                            emptyMessage="Nenhum medicamento no modelo — o texto da receita é aplicado sozinho."
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Texto do modelo{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <RichTextEditor
                                        content={form.content}
                                        onChange={content =>
                                            setForm(previous => ({
                                                ...previous,
                                                content,
                                            }))
                                        }
                                        variables={variables}
                                        minHeight="280px"
                                        placeholder="Escreva o texto padrão. Use os botões acima para inserir o nome do paciente, a data, o CID..."
                                    />
                                    <p className="text-xs text-base-content/50 mt-2">
                                        As variáveis são trocadas pelos dados
                                        reais quando o documento é emitido.
                                    </p>
                                </div>
                            )}

                            <div className="modal-action">
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={closeModal}
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving}
                                >
                                    {saving
                                        ? 'Salvando...'
                                        : editingId
                                          ? 'Salvar alterações'
                                          : copyingFrom
                                            ? 'Salvar cópia'
                                            : 'Criar modelo'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setDeleteTarget(null)
                }}
                onConfirm={confirmDelete}
                title="Remover modelo"
                message={`Tem certeza que deseja remover o modelo "${deleteTarget?.name ?? ''}"?`}
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
